/**
 * CurrencySwitcher — dropdown for /pricing to pick a display currency.
 *
 * Auto-defaults from browser locale on first visit (via `guessCurrency`),
 * persists to localStorage, and emits the chosen ISO-4217 code to a parent
 * via `onChange`. Rates + symbols come from GET /api/fx/rates (public).
 *
 * Rendered next to the LanguageSwitcher on Pricing.jsx so the two live
 * side by side. AUD is always present; if the currency dropdown is set to
 * AUD the parent renders only the primary A$ price with no muted subline.
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { CurrencyDollar, CaretDown } from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

// Browser locale (e.g. `de-DE`, `zh-CN`, `en-AU`) → default currency.
// Everything else falls back to USD so international visitors don't see AUD
// (their home currency stays the primary visual cue while AUD remains the
// billing floor). If a user is on en-AU they see AUD both places.
const LOCALE_TO_CURRENCY = {
  'en-AU': 'AUD', 'en-NZ': 'NZD', 'en-US': 'USD', 'en-GB': 'GBP',
  'en-CA': 'CAD', 'en-IN': 'INR', 'en-AE': 'AED',
  'de': 'EUR', 'de-DE': 'EUR', 'de-AT': 'EUR', 'de-CH': 'EUR',
  'fr': 'EUR', 'fr-FR': 'EUR', 'fr-CA': 'CAD',
  'es': 'EUR', 'es-ES': 'EUR', 'es-MX': 'USD',
  'it': 'EUR', 'nl': 'EUR', 'pt': 'EUR', 'pt-BR': 'USD',
  'zh': 'CNY', 'zh-CN': 'CNY', 'zh-TW': 'USD', 'zh-HK': 'USD',
  'ja': 'JPY', 'ja-JP': 'JPY',
  'ar': 'AED', 'ar-AE': 'AED', 'ar-SA': 'USD',
  'hi': 'INR', 'hi-IN': 'INR',
};

export function guessCurrency(supported = []) {
  if (typeof navigator === 'undefined') return 'AUD';
  const langs = navigator.languages && navigator.languages.length
    ? navigator.languages
    : [navigator.language || 'en-AU'];
  for (const l of langs) {
    if (!l) continue;
    // Normalise locale — strip POSIX / codeset suffixes ("en-US@posix",
    // "de_DE.UTF-8", etc.) and coerce underscore to hyphen so mapping keys
    // match reliably across Chromium / Playwright / embedded browsers.
    const norm = l.split('@')[0].split('.')[0].replace('_', '-');
    if (LOCALE_TO_CURRENCY[norm] && supported.includes(LOCALE_TO_CURRENCY[norm])) return LOCALE_TO_CURRENCY[norm];
    const base = norm.split('-')[0];
    if (LOCALE_TO_CURRENCY[base] && supported.includes(LOCALE_TO_CURRENCY[base])) return LOCALE_TO_CURRENCY[base];
  }
  return 'AUD';
}

export default function CurrencySwitcher({ value, onChange, fxData }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, []);

  const supported = fxData?.supported || ['AUD'];
  const symbol = (fxData?.symbols || {})[value] || value;

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="currency-switcher"
        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm border border-border bg-card text-foreground hover:bg-accent font-semibold uppercase tracking-widest rounded-md transition-colors"
        title="Display currency"
      >
        <CurrencyDollar size={14} />
        <span className="font-mono">{value}</span>
        <span className="text-muted-foreground">·</span>
        <span className="font-mono">{symbol}</span>
        <CaretDown size={12} />
      </button>
      {open && (
        <div
          className="absolute end-0 top-full mt-1 min-w-[160px] border border-border bg-popover text-popover-foreground shadow-lg z-50 rounded-md"
          data-testid="currency-dropdown"
        >
          {supported.map((code) => {
            const s = (fxData?.symbols || {})[code] || code;
            return (
              <button
                key={code}
                onClick={() => { onChange(code); setOpen(false); try { localStorage.setItem('stockaris_currency', code); } catch (_) { /* ignore */ } }}
                data-testid={`currency-option-${code}`}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between ${value === code ? 'bg-brand text-brand-foreground' : 'hover:bg-accent'}`}
              >
                <span className="font-mono">{code}</span>
                <span className="font-mono text-xs opacity-70">{s}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/**
 * Convert an AUD amount to the target currency using the fxData rates
 * table. Returns the raw number; caller formats. AUD → AUD passes through
 * as-is. Missing rate → null (caller renders nothing).
 */
export function convertFromAud(amountAud, currency, fxData) {
  if (amountAud == null) return null;
  if (currency === 'AUD') return amountAud;
  const rate = (fxData?.rates || {})[currency];
  if (!rate) return null;
  return amountAud * rate;
}

/**
 * Format an already-converted number for display. Uses Intl.NumberFormat
 * so JPY (no decimals), INR (grouped), etc. render naturally.
 */
export function formatMoney(amount, currency, fxData) {
  if (amount == null) return '';
  const symbol = (fxData?.symbols || {})[currency] || '';
  // JPY / INR use whole units; others 2 dp for tiers < 100, 0 dp above.
  const noDecimals = currency === 'JPY' || amount >= 100;
  const nf = new Intl.NumberFormat(undefined, {
    minimumFractionDigits: noDecimals ? 0 : 2,
    maximumFractionDigits: noDecimals ? 0 : 2,
  });
  return `${symbol}${nf.format(amount)}`;
}

export const CURRENCY_FETCH = () => axios.get(`${API}/api/fx/rates`).then((r) => r.data);
